import React from 'react';
import { 
  Info, 
  Target, 
  Cpu, 
  Star, 
  BarChart3, 
  Calculator, 
  TrendingUp, 
  Users,
  Clock,
  Activity,
  Zap,
  BookOpen
} from 'lucide-react';

export const ReadmeTab: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Info className="w-8 h-8 text-primary-400 mr-3" />
          <h1 className="text-3xl font-bold text-gray-100">About This Platform</h1>
        </div>
        <p className="text-gray-400 max-w-3xl mx-auto text-lg">
          A comprehensive queueing theory analysis platform for understanding and optimizing service systems 
          through mathematical modeling and real-time visualization.
        </p>
      </div>

      {/* Purpose */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <Target className="w-6 h-6 text-primary-400 mr-3" />
          <h2 className="text-xl font-semibold text-gray-200">Purpose</h2>
        </div>
        <p className="text-gray-300 mb-4">
          This platform provides an interactive environment for analyzing queueing systems using established mathematical principles. 
          It serves as both an educational tool for learning queueing theory concepts and a practical analyzer for real-world service system optimization.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-primary-400 mb-2">Educational Focus</h4>
            <p className="text-gray-300 text-sm">
              Learn fundamental queueing theory concepts through interactive examples and comprehensive theory sections.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-accent-400 mb-2">Practical Application</h4>
            <p className="text-gray-300 text-sm">
              Analyze real service systems to optimize performance, reduce wait times, and improve resource utilization.
            </p>
          </div>
        </div>
      </div>

      {/* Technology Stack */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <Cpu className="w-6 h-6 text-accent-400 mr-3" />
          <h2 className="text-xl font-semibold text-gray-200">Technology Stack</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-3">
            <h4 className="font-semibold text-primary-400">Frontend</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                React 19 + TypeScript
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                Tailwind CSS
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                Lucide React Icons
              </li>
            </ul>
          </div>
          <div className="space-y-3">
            <h4 className="font-semibold text-accent-400">Mathematics</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                KaTeX for LaTeX rendering
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                VUT Equation implementation
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                Monte Carlo simulation
              </li>
            </ul>
          </div>
          <div className="space-y-3">
            <h4 className="font-semibold text-green-400">Visualization</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Recharts library
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Interactive charts
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                Real-time updates
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <Star className="w-6 h-6 text-primary-400 mr-3" />
          <h2 className="text-xl font-semibold text-gray-200">Features</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center mb-3">
              <Calculator className="w-5 h-5 text-primary-400 mr-2" />
              <h4 className="font-semibold text-gray-200">Interactive Analysis</h4>
            </div>
            <p className="text-gray-300 text-sm">
              Real-time parameter adjustment with instant recalculation of all performance metrics and visualizations.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center mb-3">
              <BarChart3 className="w-5 h-5 text-accent-400 mr-2" />
              <h4 className="font-semibold text-gray-200">Rich Visualizations</h4>
            </div>
            <p className="text-gray-300 text-sm">
              Multiple chart types including utilization curves, variability analysis, and sensitivity studies.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center mb-3">
              <TrendingUp className="w-5 h-5 text-green-400 mr-2" />
              <h4 className="font-semibold text-gray-200">Performance Metrics</h4>
            </div>
            <p className="text-gray-300 text-sm">
              Comprehensive KPI dashboard showing utilization, queue times, WIP, throughput, and system efficiency.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center mb-3">
              <Users className="w-5 h-5 text-blue-400 mr-2" />
              <h4 className="font-semibold text-gray-200">Multi-Server Support</h4>
            </div>
            <p className="text-gray-300 text-sm">
              Analyze both single-server and multi-server queueing systems with server pooling comparisons.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center mb-3">
              <Activity className="w-5 h-5 text-purple-400 mr-2" />
              <h4 className="font-semibold text-gray-200">Variability Analysis</h4>
            </div>
            <p className="text-gray-300 text-sm">
              Study the impact of arrival and service variability on system performance across different scenarios.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center mb-3">
              <Zap className="w-5 h-5 text-yellow-400 mr-2" />
              <h4 className="font-semibold text-gray-200">Simulation Engine</h4>
            </div>
            <p className="text-gray-300 text-sm">
              Monte Carlo simulation capabilities to validate analytical results and explore system dynamics.
            </p>
          </div>
        </div>
      </div>

      {/* Model Highlights */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <BookOpen className="w-6 h-6 text-accent-400 mr-3" />
          <h2 className="text-xl font-semibold text-gray-200">Model Highlights</h2>
        </div>
        <div className="space-y-4">
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-primary-400 mb-2">VUT Equation Implementation</h4>
            <p className="text-gray-300 text-sm mb-2">
              Kingman's VUT (Variability-Utilization-Time) equation for both G/G/1 and G/G/m systems:
            </p>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• Single server: Exact formulas for M/M/1, M/D/1, and D/D/1 special cases</li>
              <li>• Multi-server: G/G/m approximation with server pooling effects</li>
              <li>• Comprehensive error handling for unstable systems (utilization ≥ 100%)</li>
            </ul>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-accent-400 mb-2">Little's Law Integration</h4>
            <p className="text-gray-300 text-sm">
              Seamless integration of Little's Law (WIP = Throughput × Cycle Time) to ensure consistency 
              across all performance metrics and provide multiple validation perspectives.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-green-400 mb-2">Kendall Notation Support</h4>
            <p className="text-gray-300 text-sm">
              Full support for standard queueing theory notation (X/Y/m/b) with automatic system classification 
              and appropriate formula selection based on arrival and service distributions.
            </p>
          </div>
        </div>
      </div>

      {/* Educational Value */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <Clock className="w-6 h-6 text-primary-400 mr-3" />
          <h2 className="text-xl font-semibold text-gray-200">Educational Value</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-primary-400">Learning Objectives</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Understand queueing system fundamentals and performance metrics
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Learn the impact of variability on system performance
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Explore server pooling benefits and capacity planning
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Apply mathematical models to real-world scenarios
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h4 className="font-semibold text-accent-400">Key Concepts Covered</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Coefficient of variation and its impact on queue performance
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Utilization curves and system stability boundaries
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Trade-offs between capacity, variability, and performance
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-accent-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Sensitivity analysis for operational decision making
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">Getting Started</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="text-center mb-3">
              <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">1</div>
              <h4 className="font-semibold text-gray-200">Set Parameters</h4>
            </div>
            <p className="text-gray-300 text-sm text-center">
              Adjust arrival rates, service rates, variability coefficients, and number of servers to match your system.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="text-center mb-3">
              <div className="w-8 h-8 bg-accent-500 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">2</div>
              <h4 className="font-semibold text-gray-200">Analyze Results</h4>
            </div>
            <p className="text-gray-300 text-sm text-center">
              Review the comprehensive KPI dashboard and explore different chart visualizations to understand system behavior.
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <div className="text-center mb-3">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">3</div>
              <h4 className="font-semibold text-gray-200">Optimize</h4>
            </div>
            <p className="text-gray-300 text-sm text-center">
              Use sensitivity analysis and scenario comparisons to identify optimization opportunities and make informed decisions.
            </p>
          </div>
        </div>
      </div>

      {/* Academic Foundation */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">Academic Foundation</h2>
        <p className="text-gray-300 mb-4">
          This platform is built upon established queueing theory principles from leading academic institutions and textbooks:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-primary-400 mb-2">MIT Supply Chain Management</h4>
            <p className="text-gray-300 text-xs">
              Based on materials from MIT Center for Transportation & Logistics MicroMasters program
            </p>
          </div>
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
            <h4 className="font-semibold text-accent-400 mb-2">Factory Physics</h4>
            <p className="text-gray-300 text-xs">
              Implements principles from Hopp & Spearman's Factory Physics framework
            </p>
          </div>
        </div>
      </div>


      {/* Footer */}
      <div className="text-center pt-8 border-t border-dark-700">
        <p className="text-gray-400">
          Built with ❤️ for operations research and queueing theory education
        </p>
        <p className="text-gray-500 text-sm mt-2">
          Fernando Izeta • August 2025
        </p>
        <p className="text-gray-500 text-sm">
          © 2025 IKUTU - Theory Analysis Platform. Educational use encouraged.
        </p>
      </div>
    </div>
  );
};